import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Pagination,
    Card,
    CardContent,
    Grid,
    MenuItem
} from "@mui/material";
import { EditOutlined, DeleteOutline } from "@mui/icons-material";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import Swal from "sweetalert2";
import moment from "moment/moment";
import MicIcon from "@mui/icons-material/Mic";
import useVoiceInput from "../hooks/useVoiceInput";

const PAGE_SIZE = 10;

function Customer() {
    const [customers, setCustomers] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    
    const [productList, setProductList] = useState([]); // all products
    const [throughList, setThroughList] = useState([]); // ✅ NEW: Through Details list
    
    const [customerProducts, setCustomerProducts] = useState([
        { productId: "", productName: "", rate: 0 }
    ]);
    const [customerVehicles, setCustomerVehicles] = useState([
        { vehicleNo: "", brassWeight: 0 }
    ]);

    const [form, setForm] = useState({
        customerName: "",
        mobile: "",
        email: "",
        address: "",
        city: "",
        state: "",
        gstName: "",
        gstNo: "",
        dailyCreditLimit: 0,
        monthlyCreditLimit: 0,
        reminderDate: null,
        reminderAmount: 0,
        throughId: "",   // ✅ NEW: Assigned Through ID
        throughName: ""  // ✅ NEW: Assigned Through Name
    });

    const voice = useVoiceInput((field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value
        }));
    });

    // Fetch products, through details, and customers on mount
    useEffect(() => {
        loadProducts();
        loadThroughDetails(); // ✅ NEW
        loadCustomers();
    }, []);

    const loadProducts = async () => {
        const snap = await getDocs(collection(db, "trip_products"));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setProductList(data);
    };

    // ✅ NEW: Load Through Details from Firestore
    const loadThroughDetails = async () => {
        try {
            const snap = await getDocs(collection(db, "throughdetails"));
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setThroughList(data);
        } catch (error) {
            console.error("Error fetching through details:", error);
        }
    };

    const loadCustomers = async () => {
        try {
            const customersRef = collection(db, "customers");
            const q = query(customersRef, orderBy("customerName", "asc"));
            const snap = await getDocs(q);

            setCustomers(snap.docs.map(d => ({
                id: d.id,
                ...d.data()
            })));
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        if (!form.customerName || !form.mobile) {
            Swal.fire("Error", "Please fill required fields", "error");
            return;
        }

        const finalData = {
            ...form,
            products: customerProducts,
            dailyCreditLimit: Number(form.dailyCreditLimit || 0),
            monthlyCreditLimit: Number(form.monthlyCreditLimit || 0),
            vehicles: customerVehicles,
        };

        if (editingId) {
            await updateDoc(doc(db, "customers", editingId), finalData);
            Swal.fire("Update", "Customer updated", "success");
        } else {
            await addDoc(collection(db, "customers"), finalData);
            Swal.fire("Added", "Customer created", "success");
        }

        loadCustomers();
        setOpenDialog(false);
    };

    const handleEdit = (cust) => {
        setEditingId(cust.id);
        setForm({
            ...cust,
            throughId: cust.throughId || "",
            throughName: cust.throughName || ""
        });

        setCustomerProducts(
            cust.products && cust.products.length > 0
                ? cust.products
                : [{ productId: "", productName: "", rate: 0 }]
        );
        setCustomerVehicles(
            cust.vehicles && cust.vehicles.length > 0
                ? cust.vehicles
                : [{ vehicleNo: "", brassWeight: 0 }]
        );

        setOpenDialog(true);
    };

    const handleDelete = async (id) => {
        const res = await Swal.fire({
            title: "Delete customer?",
            icon: "warning",
            showCancelButton: true,
        });
        if (!res.isConfirmed) return;
        await deleteDoc(doc(db, "customers", id));
        Swal.fire("Deleted", "Customer removed", "success");
        loadCustomers();
    };

    // Filter & paginate
    const filtered = customers.filter(
        c =>
            c.customerName?.toLowerCase().includes(search.toLowerCase()) ||
            c.mobile?.includes(search) ||
            c.email?.toLowerCase().includes(search.toLowerCase())
    );

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box sx={{
            bgcolor: '#f4f6f8',
            minHeight: '100vh',
            p: 1,
            "& .MuiInputBase-root": { fontSize: "14px" },
            "& .MuiTableCell-root": { fontSize: "13px", padding: "6px 6px" },
            "& .MuiButton-root": { fontSize: "11px", padding: "4px 8px" }
        }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={800}>
                    Customer Management
                </Typography>
                <Button variant="contained" onClick={() => {
                    setEditingId(null);
                    setForm({
                        customerName: "", mobile: "", email: "", address: "",
                        city: "", state: "", gstName: "", gstNo: "",
                        dailyCreditLimit: 0, monthlyCreditLimit: 0,
                        reminderDate: null, reminderAmount: 0,
                        throughId: "", throughName: ""
                    });
                    setCustomerProducts([{ productId: "", productName: "", rate: 0 }]);
                    setCustomerVehicles([{ vehicleNo: "", brassWeight: 0 }]);
                    setOpenDialog(true);
                }}>
                    Add New Customer
                </Button>
            </Box>

            <TextField
                label="Search by name, mobile, email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
            />

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Mobile</TableCell>
                                <TableCell>Through</TableCell> {/* ✅ NEW COLUMN */}
                                <TableCell>Address</TableCell>
                                <TableCell>City</TableCell>
                                <TableCell>Daily Limit</TableCell>
                                <TableCell>Monthly Limit</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.map((c, index) => (
                                <TableRow key={c.id}>
                                    <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                                    <TableCell>{c.custcode}</TableCell>
                                    <TableCell>{c.customertype}</TableCell>
                                    <TableCell>{c.customerName}</TableCell>
                                    <TableCell>{c.mobile}</TableCell>
                                    <TableCell>{c.throughName || "-"}</TableCell> {/* ✅ DISPLAY THROUGH NAME */}
                                    <TableCell>{c.address || "-"}</TableCell>
                                    <TableCell>{c.city || "-"}</TableCell>
                                    <TableCell>₹ {c.dailyCreditLimit || 0}</TableCell>
                                    <TableCell>₹ {c.monthlyCreditLimit || 0}</TableCell>
                                    <TableCell align="center">
                                        <IconButton color="primary" onClick={() => handleEdit(c)}>
                                            <EditOutlined />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(c.id)}>
                                            <DeleteOutline />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            count={Math.ceil(filtered.length / PAGE_SIZE)}
                            page={page}
                            onChange={(e, v) => setPage(v)}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editingId ? "Edit Customer" : "Add New Customer"}</DialogTitle>
                <DialogContent>
                    <Grid className="row" spacing={2} sx={{ mt: 1 }}>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="Customer Code"
                                name="custcode"
                                value={form.custcode || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="Customer Type"
                                name="customertype"
                                value={form.customertype || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>

                        {/* ✅ NEW: Assign Through Details Dropdown */}
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={6}>
                            <TextField
                                select
                                label="Through Detail (Reference)"
                                name="throughId"
                                value={form.throughId || ""}
                                onChange={(e) => {
                                    const selectedId = e.target.value;
                                    const found = throughList.find(t => t.id === selectedId);
                                    setForm(prev => ({
                                        ...prev,
                                        throughId: selectedId,
                                        throughName: found ? found.name : ""
                                    }));
                                }}
                                fullWidth
                                size="small"
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {throughList.map(t => (
                                    <MenuItem key={t.id} value={t.id}>
                                        {t.name} ({t.mobile})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="Customer Name"
                                name="customerName"
                                value={form.customerName || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="Mobile"
                                name="mobile"
                                value={form.mobile || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="Email"
                                name="email"
                                value={form.email || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="Address"
                                name="address"
                                value={form.address || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="City"
                                name="city"
                                value={form.city || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="State"
                                name="state"
                                value={form.state || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="GST Name"
                                name="gstName"
                                value={form.gstName || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="GST No"
                                name="gstNo"
                                value={form.gstNo || ""}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="Daily Credit Limit"
                                name="dailyCreditLimit"
                                type="number"
                                value={form.dailyCreditLimit || 0}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                            <TextField
                                label="Monthly Credit Limit"
                                name="monthlyCreditLimit"
                                type="number"
                                value={form.monthlyCreditLimit || 0}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 2 }}>
                        <Typography fontWeight={700}>Customer Product Rates</Typography>
                    </Box>

                    {customerProducts.map((row, index) => (
                        <Grid container spacing={1} key={index} alignItems="center" sx={{ mt: 1 }}>
                            <Grid className="col-sm-3 mb-3" item xs={12} sm={5}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Product"
                                    value={row.productId || ""}
                                    onChange={(e) => {
                                        const selected = productList.find(p => p.id === e.target.value);
                                        const updated = [...customerProducts];
                                        updated[index] = {
                                            productId: selected.id,
                                            productName: selected.productName,
                                            rate: selected.rate || 0
                                        };
                                        setCustomerProducts(updated);
                                    }}
                                >
                                    {productList.map(p => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.productName}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid className="col-sm-3 mb-3" item xs={12} sm={4}>
                                <TextField
                                    label="Rate"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={row.rate || 0}
                                    onChange={(e) => {
                                        const updated = [...customerProducts];
                                        updated[index].rate = e.target.value;
                                        setCustomerProducts(updated);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Button onClick={() => {
                                    setCustomerProducts([
                                        ...customerProducts,
                                        { productId: "", productName: "", rate: 0 }
                                    ]);
                                }}>
                                    +
                                </Button>
                                {customerProducts.length > 1 && (
                                    <Button color="error" onClick={() => {
                                        const updated = customerProducts.filter((_, i) => i !== index);
                                        setCustomerProducts(updated);
                                    }}>
                                        -
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    ))}

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>
                        {editingId ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Customer;